
import React, { useState } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Gift, Check, X } from 'lucide-react';

interface ReferralCodeInputProps {
  onReferralCodeChange: (code: string, isValid: boolean) => void;
  userType?: 'internal' | 'supabase';
}

const ReferralCodeInput: React.FC<ReferralCodeInputProps> = ({ 
  onReferralCodeChange, 
  userType = 'supabase' 
}) => {
  const [referralCode, setReferralCode] = useState('');
  const [isValidating, setIsValidating] = useState(false);
  const [validationStatus, setValidationStatus] = useState<'valid' | 'invalid' | 'unchecked'>('unchecked');

  const validateReferralCode = async (code: string) => {
    if (!code.trim()) {
      setValidationStatus('unchecked');
      onReferralCodeChange('', false);
      return;
    }

    setIsValidating(true);
    
    try {
      const { supabase } = await import('@/integrations/supabase/client');
      
      // Check both internal and Supabase users for referral codes
      const { data: supabaseData } = await supabase
        .from('user_points')
        .select('user_id, user_type')
        .eq('referral_code', code.toUpperCase())
        .single();

      if (supabaseData) {
        setValidationStatus('valid');
        onReferralCodeChange(code, true);
      } else {
        // Also check internal users if not found in Supabase users
        try {
          const { internalAuthService } = await import('@/services/internalAuthService');
          const result = await internalAuthService.validateReferralCode(code.toUpperCase());
          
          if (result.valid) {
            setValidationStatus('valid');
            onReferralCodeChange(code, true);
          } else {
            setValidationStatus('invalid');
            onReferralCodeChange(code, false);
          }
        } catch (error) {
          console.error('Error validating internal referral code:', error);
          setValidationStatus('invalid');
          onReferralCodeChange(code, false);
        }
      }
    } catch (error) {
      console.error('Error validating referral code:', error);
      setValidationStatus('invalid');
      onReferralCodeChange(code, false);
    } finally {
      setIsValidating(false);
    }
  };

  const handleInputChange = (value: string) => {
    const formattedCode = value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 8);
    setReferralCode(formattedCode);
    
    if (formattedCode.length >= 6) {
      validateReferralCode(formattedCode);
    } else {
      setValidationStatus('unchecked');
      onReferralCodeChange(formattedCode, false);
    }
  };

  const getValidationIcon = () => {
    if (isValidating) {
      return <div className="animate-spin h-4 w-4 border-2 border-gray-300 border-t-sports-green rounded-full" />;
    }
    
    switch (validationStatus) {
      case 'valid':
        return <Check className="h-4 w-4 text-green-500" />;
      case 'invalid':
        return <X className="h-4 w-4 text-red-500" />;
      default:
        return <Gift className="h-4 w-4 text-gray-400" />;
    }
  };

  const getValidationMessage = () => {
    switch (validationStatus) {
      case 'valid':
        return <span className="text-green-500 text-sm">Valid referral code! You'll get 50 bonus points.</span>;
      case 'invalid':
        return <span className="text-red-500 text-sm">Invalid referral code. Please check and try again.</span>;
      default:
        return <span className="text-gray-400 text-sm">Enter a friend's referral code to get bonus points (optional)</span>;
    }
  };

  return (
    <div className="space-y-2">
      <Label htmlFor="referral-code" className="text-white flex items-center gap-2">
        <Gift className="h-4 w-4" />
        Referral Code (Optional)
      </Label>
      <div className="relative">
        <Input
          id="referral-code"
          name="referral-code"
          value={referralCode}
          onChange={(e) => handleInputChange(e.target.value)}
          placeholder="Enter code (e.g. ABC123DE)"
          className="bg-gray-700 border-gray-600 text-white pr-10 font-mono text-center tracking-wider"
          maxLength={8}
        />
        <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
          {getValidationIcon()}
        </div>
      </div>
      {getValidationMessage()}
    </div>
  );
};

export default ReferralCodeInput;
